import { useEffect, useRef, useState } from "react";

type InstallEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function InstallApp({ visible }: { readonly visible: boolean }) {
  const prompt = useRef<InstallEvent | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [busy, setBusy] = useState(false);
  const ios =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(navigator.userAgent);
  useEffect(() => {
    const capture = (event: Event) => {
      event.preventDefault();
      prompt.current = event as InstallEvent;
    };
    const complete = () => {
      prompt.current = null;
      setInstalled(true);
      dialog.current?.close();
    };
    const media = window.matchMedia("(display-mode: standalone)");
    const change = () => {
      if (isStandalone()) complete();
    };
    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", complete);
    media.addEventListener("change", change);
    return () => {
      window.removeEventListener("beforeinstallprompt", capture);
      window.removeEventListener("appinstalled", complete);
      media.removeEventListener("change", change);
    };
  }, []);
  async function install() {
    if (busy) return;
    if (ios || !prompt.current) {
      dialog.current?.showModal();
      return;
    }
    const event = prompt.current;
    prompt.current = null;
    setBusy(true);
    try {
      await event.prompt();
      await event.userChoice;
    } catch {
      dialog.current?.showModal();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div hidden={!visible} className="install-app">
      <button
        type="button"
        className="more-menu-row install-app__link"
        disabled={busy || installed}
        onClick={() => void install()}
      >
        <span className="more-menu-row__icon" aria-hidden="true">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path d="M12 3v12m-4-4 4 4 4-4M4 15v6h16v-6" />
          </svg>
        </span>
        <span>
          <strong>{installed ? "Added to Home Screen" : "Add to Home Screen"}</strong>
        </span>
        <b aria-hidden="true">›</b>
      </button>
      <dialog
        ref={dialog}
        className="hotel-detail-dialog admin-driver-detail install-app__dialog"
        aria-labelledby="install-app-title"
      >
        <header className="hotel-detail-dialog__header">
          <h2 id="install-app-title">Add to Home Screen</h2>
          <button
            type="button"
            className="service-dialog__close"
            aria-label="Close installation instructions"
            onClick={() => dialog.current?.close()}
          >
            ×
          </button>
        </header>
        <div className="hotel-detail-dialog__content">
          {ios ? (
            <>
              <p>On your iPhone or iPad:</p>
              <ol>
                <li>Open this app’s website in Safari.</li>
                <li>
                  Tap Share (the square with an upward arrow). It may be inside the More menu.
                </li>
                <li>
                  Choose <strong>Add to Home Screen</strong>. If it is missing, look under Edit
                  Actions.
                </li>
                <li>
                  Keep <strong>Open as Web App</strong> enabled if shown, then tap{" "}
                  <strong>Add</strong>.
                </li>
              </ol>
            </>
          ) : android ? (
            <>
              <p>In Chrome on Android:</p>
              <ol>
                <li>Open the browser menu (⋮).</li>
                <li>
                  Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.
                </li>
                <li>
                  Confirm by tapping <strong>Add</strong> or <strong>Install</strong>.
                </li>
              </ol>
              <p>
                If you opened this link inside another app, open it in Chrome first. The install
                option depends on your browser and may not appear if the app is already installed.
              </p>
            </>
          ) : (
            <p>
              Use your browser’s install icon in the address bar, or find “Install app” in its menu.
              If your browser does not offer installation, open this site in a supported browser
              such as Chrome or Edge.
            </p>
          )}
          <button
            type="button"
            className="button button--primary"
            onClick={() => dialog.current?.close()}
          >
            Got it
          </button>
        </div>
      </dialog>
    </div>
  );
}
